// src/pages/finance-portal/budgets/BudgetForm.js
//
// Budget header form (create / edit). Lines are managed on
// BudgetDetail page. Validation mirrors backend so form errors paint inline.

import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert,
    CircularProgress, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const METHOD_OPTIONS = [
    { value: 'TOP_DOWN', label: 'Top-Down (Executive)' },
    { value: 'BOTTOM_UP', label: 'Bottom-Up (Departments)' },
    { value: 'HISTORICAL', label: 'Historical (Prior Year + %)' },
];

const initial = {
    name: '',
    description: '',
    fiscal_year: new Date().getFullYear(),
    period_start: '',
    period_end: '',
    method: 'TOP_DOWN',
    currency: 'USD',
    due_date: '',
    notes: '',
    initial_version_label: 'Original',
};

export default function BudgetForm({ open, onClose, onSaved, budget }) {
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState({ ...initial });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const isEdit = !!budget;

    useEffect(() => {
        if (open) {
            setErrors({});
            if (budget) {
                setData({
                    name: budget.name || '',
                    description: budget.description || '',
                    fiscal_year: budget.fiscal_year || new Date().getFullYear(),
                    period_start: budget.period_start || '',
                    period_end: budget.period_end || '',
                    method: budget.method || 'TOP_DOWN',
                    currency: budget.currency || 'USD',
                    due_date: budget.due_date || '',
                    notes: budget.notes || '',
                    initial_version_label: '',  // not editable on update
                });
            } else {
                const y = new Date().getFullYear();
                setData({
                    ...initial,
                    fiscal_year: y,
                    period_start: `${y}-01-01`,
                    period_end: `${y}-12-31`,
                });
            }
        }
    }, [open, budget]);

    const update = (k, v) => setData(prev => ({ ...prev, [k]: v }));

    const validateLocal = () => {
        const e = {};
        if (!data.name?.trim()) e.name = 'Required';
        const fy = parseInt(data.fiscal_year, 10);
        if (isNaN(fy) || fy < 1900 || fy > 2200) e.fiscal_year = 'Must be 1900–2200';
        if (!data.period_start) e.period_start = 'Required';
        if (!data.period_end) e.period_end = 'Required';
        if (data.period_start && data.period_end && data.period_end < data.period_start) {
            e.period_end = 'Period end must be on or after period start';
        }
        return e;
    };

    const handleSave = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                name: data.name.trim(),
                description: data.description?.trim() || null,
                fiscal_year: parseInt(data.fiscal_year, 10),
                period_start: data.period_start,
                period_end: data.period_end,
                method: data.method,
                currency: (data.currency || 'USD').toUpperCase(),
                due_date: data.due_date || null,
                notes: data.notes?.trim() || null,
            };
            if (!isEdit && data.initial_version_label) {
                payload.initial_version_label = data.initial_version_label;
            }

            if (isEdit) {
                await axiosInstance.put(`/api/finance/budgets/${budget.id}`, payload);
                enqueueSnackbar('Budget updated', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/budgets', payload);
                enqueueSnackbar('Budget created', { variant: 'success' });
            }
            onSaved?.();
            onClose();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar(msg, { variant: 'warning' });
            } else {
                enqueueSnackbar(msg, { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex',
                              alignItems: 'center', justifyContent: 'space-between' }}>
                {isEdit ? 'Edit Budget' : 'New Budget'}
                <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                        label="Name *" size="small" fullWidth
                        value={data.name}
                        onChange={e => update('name', e.target.value)}
                        error={!!errors.name} helperText={errors.name || ' '}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <TextField
                        label="Description" size="small" fullWidth multiline rows={2}
                        value={data.description}
                        onChange={e => update('description', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="Fiscal Year *" size="small" type="number" sx={{ minWidth: 150 }}
                            value={data.fiscal_year}
                            onChange={e => update('fiscal_year', e.target.value)}
                            error={!!errors.fiscal_year} helperText={errors.fiscal_year || ' '}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={labelSx}>Method</InputLabel>
                            <Select value={data.method} label="Method"
                                    onChange={e => update('method', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {METHOD_OPTIONS.map(o => (
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="Period Start *" size="small" fullWidth type="date"
                            value={data.period_start}
                            onChange={e => update('period_start', e.target.value)}
                            error={!!errors.period_start} helperText={errors.period_start || ' '}
                            InputLabelProps={{ ...labelSx, shrink: true, sx: labelSx }}
                            InputProps={{ sx: fieldSx }}
                        />
                        <TextField
                            label="Period End *" size="small" fullWidth type="date"
                            value={data.period_end}
                            onChange={e => update('period_end', e.target.value)}
                            error={!!errors.period_end} helperText={errors.period_end || ' '}
                            InputLabelProps={{ ...labelSx, shrink: true, sx: labelSx }}
                            InputProps={{ sx: fieldSx }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="Currency" size="small" sx={{ width: 120 }}
                            value={data.currency}
                            onChange={e => update('currency', e.target.value.toUpperCase())}
                            inputProps={{ maxLength: 3 }}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="Due Date" size="small" fullWidth type="date"
                            value={data.due_date}
                            onChange={e => update('due_date', e.target.value)}
                            InputLabelProps={{ ...labelSx, shrink: true, sx: labelSx }}
                            InputProps={{ sx: fieldSx }}
                        />
                    </Box>
                    {!isEdit && (
                        <TextField
                            label="Initial Version Label" size="small" fullWidth
                            value={data.initial_version_label}
                            onChange={e => update('initial_version_label', e.target.value)}
                            placeholder="e.g. Original, Q1 Plan"
                            helperText='Default: "Original" — name the first version of this budget'
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                            FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }}
                        />
                    )}
                    <TextField
                        label="Notes" size="small" fullWidth multiline rows={2}
                        value={data.notes}
                        onChange={e => update('notes', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained" onClick={handleSave} disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                          bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                    {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Budget'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
